/**
 * Investor Notifications Page
 * 
 * Shows payment received notifications, portfolio updates, and system alerts.
 * Displays a chronological activity feed for the investor.
 */
import { useInvestorAuth } from "@/hooks/useInvestorAuth";
import InvestorLayout from "@/components/InvestorLayout";
import { trpc } from "@/lib/trpc";
import {
  Bell,
  DollarSign,
  Briefcase,
  CheckCircle2,
  AlertCircle,
  Info,
  Clock,
} from "lucide-react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.1)",
  gold: "#f59e0b",
  goldLight: "rgba(245,158,11,0.1)",
  green: "#10b981",
  greenLight: "rgba(16,185,129,0.1)",
  red: "#ef4444",
  redLight: "rgba(239,68,68,0.1)",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

type NotificationItem = {
  id: string;
  type: "payment_received" | "portfolio_added" | "payment_upcoming" | "info";
  title: string;
  description: string;
  date: string;
  amount?: number;
};

function buildNotifications(
  payments: any[],
  portfolios: any[],
  schedule: any
): NotificationItem[] {
  const items: NotificationItem[] = [];

  // Payment received notifications
  for (const p of payments) {
    items.push({
      id: `payment-${p.id}`,
      type: "payment_received",
      title: "Payment Received",
      description: p.notes || `Payment recorded for your portfolio`,
      date: p.payment_date || p.created_at,
      amount: parseFloat(p.amount || "0"),
    });
  }

  // Portfolio added notifications
  for (const p of portfolios) {
    items.push({
      id: `portfolio-${p.id}`,
      type: "portfolio_added",
      title: "Portfolio Added",
      description: `${p.name} was added to your account`,
      date: p.created_at,
    });
  }

  // Upcoming payments (next 7 days)
  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  for (const s of schedule?.upcoming || []) {
    const dueDate = new Date(s.due_date);
    if (dueDate <= in7Days && dueDate >= now) {
      items.push({
        id: `upcoming-${s.id}`,
        type: "payment_upcoming",
        title: "Payment Due Soon",
        description: `Payment #${s.payment_number} for ${s.portfolioName || "your portfolio"} is due ${formatRelativeDate(s.due_date)}`,
        date: new Date().toISOString(),
        amount: parseFloat(s.amount || "0"),
      });
    }
  }

  // Sort by date descending
  items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return items;
}

const notificationConfig: Record<
  string,
  { icon: React.ComponentType<{ size?: number; color?: string }>; color: string; colorLight: string }
> = {
  payment_received: { icon: DollarSign, color: BRAND.green, colorLight: BRAND.greenLight },
  portfolio_added: { icon: Briefcase, color: BRAND.accent, colorLight: BRAND.accentLight },
  payment_upcoming: { icon: Clock, color: BRAND.gold, colorLight: BRAND.goldLight },
  info: { icon: Info, color: BRAND.textMuted, colorLight: "rgba(148,163,184,0.1)" },
};

function NotificationItem({ item }: { item: NotificationItem }) {
  const config = notificationConfig[item.type] || notificationConfig.info;
  const Icon = config.icon;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "16px 20px",
        borderBottom: `1px solid ${BRAND.border}`,
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,0.02)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: config.colorLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <Icon size={17} color={config.color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ color: BRAND.text, fontSize: 14, fontWeight: 600 }}>{item.title}</span>
          <span style={{ color: BRAND.textDim, fontSize: 12 }}>{formatRelativeDate(item.date)}</span>
        </div>
        <div style={{ color: BRAND.textMuted, fontSize: 13 }}>{item.description}</div>
        {item.amount && item.amount > 0 && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: config.colorLight,
              color: config.color,
              padding: "3px 10px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              marginTop: 6,
            }}
          >
            {formatCurrency(item.amount)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InvestorNotifications() {
  const { user, loading: authLoading } = useInvestorAuth({ redirectOnUnauthenticated: true });

  const paymentsQuery = trpc.investor.getPaymentsReceived.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const portfoliosQuery = trpc.investor.getPortfolios.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const scheduleQuery = trpc.investor.getPaymentSchedule.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const isLoading =
    paymentsQuery.isLoading || portfoliosQuery.isLoading || scheduleQuery.isLoading;

  const notifications = buildNotifications(
    paymentsQuery.data || [],
    portfoliosQuery.data || [],
    scheduleQuery.data
  );

  return (
    <InvestorLayout>
      <div style={{ padding: "32px 36px", maxWidth: 900 }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ color: BRAND.text, fontSize: 24, fontWeight: 700, margin: "0 0 6px 0" }}>
            Notifications
          </h1>
          <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
            Payment confirmations, portfolio updates, and upcoming payment reminders.
          </p>
        </div>

        {/* Notification Feed */}
        <div
          style={{
            background: BRAND.card,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {/* Header row */}
          <div
            style={{
              padding: "14px 20px",
              borderBottom: `1px solid ${BRAND.border}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Bell size={16} color={BRAND.accent} />
            <span style={{ color: BRAND.text, fontSize: 14, fontWeight: 700 }}>Activity Feed</span>
            {notifications.length > 0 && (
              <span
                style={{
                  background: BRAND.accentLight,
                  color: BRAND.accent,
                  padding: "2px 8px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {notifications.length}
              </span>
            )}
          </div>

          {isLoading ? (
            <div style={{ padding: "40px 20px", textAlign: "center" }}>
              <span style={{ color: BRAND.textMuted, fontSize: 14 }}>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center" }}>
              <Bell size={36} color={BRAND.textDim} style={{ marginBottom: 12 }} />
              <div style={{ color: BRAND.textMuted, fontSize: 14 }}>No notifications yet.</div>
              <div style={{ color: BRAND.textDim, fontSize: 13, marginTop: 4 }}>
                Payment confirmations and portfolio updates will appear here.
              </div>
            </div>
          ) : (
            notifications.map((item) => <NotificationItem key={item.id} item={item} />)
          )}
        </div>

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: 20,
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          {[
            { type: "payment_received", label: "Payment Received" },
            { type: "portfolio_added", label: "Portfolio Added" },
            { type: "payment_upcoming", label: "Payment Due Soon" },
          ].map((item) => {
            const config = notificationConfig[item.type];
            return (
              <div
                key={item.type}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: config.color,
                  }}
                />
                <span style={{ color: BRAND.textDim, fontSize: 12 }}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </InvestorLayout>
  );
}
